import { Controller } from "@hotwired/stimulus"

// Document-level Studio actions that live on the formatting toolbar: open the
// template drawer, toggle fit-to-one-page, and run grammar across every field.
//
// Replaces resume_toolbar_controller.js. That controller hung off
// _toolbar.html.erb, a floating rail that the Studio-layout rewrite replaced
// with _format_toolbar.html.erb without moving these three actions across — so
// all three sat in the tree, fully implemented, with nothing able to reach
// them. Its fourth action (setView, writing data-view-mode on the editor root)
// is deliberately not carried over: the CSS that made edit/focus/preview mean
// anything went out with the two-column shell, so it flipped an attribute no
// stylesheet reads. The 3-column layout plus "Finish & Preview" replaced it.
export default class extends Controller {
  static targets = ["grammarStatus"]
  static values = { grammarUrl: String }

  // _template_modal.html.erb portals itself to <body> and opens only on this
  // event, so this button is the drawer's single entry point.
  openTemplates() { window.dispatchEvent(new CustomEvent("resume:open-templates")) }

  // page_fit_controller listens on window: it scales the preview and rewrites
  // the PDF link's ?fit=1 so the export matches what the user sees.
  toggleFit() { window.dispatchEvent(new CustomEvent("resume:toggle-fit")) }

  // Runs the existing per-field /ai/grammar endpoint across every text field
  // (contenteditable summary + item details, plus the skills/section textarea),
  // applying each correction in place, then triggers a single autosave. Matches
  // the request/response contract of ai_assist_controller.js: POST JSON { text },
  // read { ok, text } from the response. Premium gating is enforced server-side.
  async grammarAll() {
    if (this.busy) return
    if (!this.hasGrammarUrlValue || !this.grammarUrlValue) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content ||
      (document.querySelector('[data-resume-editor-target="form"] input[name="authenticity_token"]') || {}).value
    // Include experience/project/education item bullets — those are
    // rendered by _item_fields with data-resume-item-field="details"
    // (contenteditable or textarea) and would otherwise be silently
    // skipped, defeating the all-document grammar action on the body
    // text users most want proofread.
    const fields = Array.from(
      document.querySelectorAll([
        '[contenteditable="true"][data-resume-field]',
        '[contenteditable="true"][data-resume-item-field="details"]',
        'textarea[data-resume-section-content]',
        'textarea[data-resume-item-field="details"]'
      ].join(","))
    ).filter((el) => ((el.isContentEditable ? el.innerHTML : el.value) || "").trim().length > 0)
    const status = this.hasGrammarStatusTarget ? this.grammarStatusTarget : null
    if (!fields.length) {
      if (status) {
        status.hidden = false
        status.textContent = "Nothing to check yet"
        setTimeout(() => { status.hidden = true }, 2500)
      }
      return
    }
    this.busy = true
    if (status) status.hidden = false
    let done = 0, errs = 0
    try {
      for (const el of fields) {
        if (status) status.textContent = `Checking ${done + 1}/${fields.length}…`
        const text = el.isContentEditable ? el.innerHTML : el.value
        try {
          const res = await fetch(this.grammarUrlValue, {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-CSRF-Token": token, "Accept": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({ text: text })
          })
          if (res.status === 403) { if (status) status.textContent = "Upgrade for AI tools"; return }
          // rack_attack throttles resume-ai/user at 25 POSTs per 60s and this
          // action spends one per field, so a long résumé can hit the ceiling
          // mid-run. Say so — counting it as a generic skip reported
          // "Done · 9 skipped" and left the cause invisible.
          if (res.status === 429) {
            if (status) status.textContent = `Rate limited at ${done}/${fields.length} — retry in a minute`
            return
          }
          if (res.ok) {
            const data = await res.json()
            const out = data && data.ok ? data.text : null
            if (out) { if (el.isContentEditable) el.innerHTML = out; else el.value = out }
            else { errs++ }
          } else { errs++ }
        } catch (_e) { errs++ }
        done++
      }
      const root = document.querySelector('[data-controller~="resume-editor"]')
      const ed = root && this.application.getControllerForElementAndIdentifier(root, "resume-editor")
      if (ed && typeof ed.scheduleSave === "function") ed.scheduleSave()
      if (status) { status.textContent = errs ? `Done · ${errs} skipped` : "Done ✓" }
    } finally {
      this.busy = false
      if (status) setTimeout(() => { status.hidden = true }, 2500)
    }
  }
}
